// pages/ManageTypingParagraphs.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { typingAPI } from '../services/api';
import ResultContext from '../context/ResultContext';
import Navbar from '../components/Navbar';
import '../styles/ManageTypingParagraphs.css';

const DIFFICULTIES = ['easy', 'medium', 'hard'];
const LANGUAGES    = ['english', 'marathi'];
const ACCESS_TYPES = ['free', 'premium'];
const TIMER_PRESETS = [30, 60, 90, 120, 180, 300];

const emptyForm = {
  title:         '',
  text:          '',
  language:      'english',
  difficulty:    'easy',
  topic:         '',
  estimatedTime: 60,
  accessType:    'free',
};

const ManageTypingParagraphs = () => {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const { setAlertContext } = useContext(ResultContext);

  const [paragraphs,   setParagraphs]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [submitting,   setSubmitting]   = useState(false);
  const [editingId,    setEditingId]    = useState(null);  // null = create mode
  const [showForm,     setShowForm]     = useState(false);
  const [form,         setForm]         = useState(emptyForm);
  const [filterLang,   setFilterLang]   = useState('');
  const [filterAccess, setFilterAccess] = useState('');

  // Subscription management state
  const [subscriptions,  setSubscriptions]  = useState([]);
  const [subEmail,       setSubEmail]       = useState('');
  const [subNote,        setSubNote]        = useState('');
  const [subLoading,     setSubLoading]     = useState(false);
  const [activeTab,      setActiveTab]      = useState('paragraphs'); // 'paragraphs' | 'subscriptions'

  // ── Guard: admin only ──────────────────────────────────────────
  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/dashboard');
  }, [user, navigate]);

  // ── Initial load ───────────────────────────────────────────────
  useEffect(() => {
    fetchParagraphs();
    fetchSubscriptions();
  }, []);

  // ── Helpers ────────────────────────────────────────────────────
  const showAlert = (message, status) => {
    setAlertContext({ isActive: true, message, status });
    setTimeout(
      () => setAlertContext((p) => ({ ...p, isActive: false })),
      3000
    );
  };

  // ── Fetch ──────────────────────────────────────────────────────
  const fetchParagraphs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterLang)   params.language   = filterLang;
      if (filterAccess) params.accessType = filterAccess;

      const res = await typingAPI.adminGetAllParagraphs(params);
      if (res.data.success) setParagraphs(res.data.paragraphs);
    } catch (err) {
      showAlert('Failed to load paragraphs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const res = await typingAPI.getAllSubscriptions();
      if (res.data.success) setSubscriptions(res.data.subscriptions);
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
    }
  };

  // Re-fetch when filters change
  useEffect(() => {
    fetchParagraphs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterLang, filterAccess]);

  // ── Form handlers ──────────────────────────────────────────────
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const openCreateForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEditForm = (para) => {
    setForm({
      title:         para.title,
      text:          para.text,
      language:      para.language,
      difficulty:    para.difficulty,
      topic:         para.topic,
      estimatedTime: para.estimatedTime,
      accessType:    para.accessType,
    });
    setEditingId(para._id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    if (!form.title.trim() || !form.text.trim() || !form.topic.trim()) {
      showAlert('Title, Text, and Topic are required', 'error');
      return;
    }
    if (form.text.trim().length < 20) {
      showAlert('Text must be at least 20 characters', 'error');
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        await typingAPI.updateParagraph(editingId, form);
        showAlert('Paragraph updated successfully ✅', 'success');
      } else {
        await typingAPI.createParagraph(form);
        showAlert('Paragraph created successfully ✅', 'success');
      }
      handleCancelForm();
      fetchParagraphs();
    } catch (err) {
      showAlert(
        err.response?.data?.message || 'Failed to save paragraph',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────
  const handleDelete = async (para) => {
    if (!window.confirm(`Delete "${para.title}"? This cannot be undone.`)) return;
    try {
      await typingAPI.deleteParagraph(para._id);
      showAlert('Paragraph deleted', 'success');
      fetchParagraphs();
    } catch (err) {
      showAlert('Failed to delete paragraph', 'error');
    }
  };

  // ── Toggle active ──────────────────────────────────────────────
  const handleToggle = async (para) => {
    try {
      await typingAPI.toggleParagraphStatus(para._id);
      showAlert(
        `Paragraph ${para.isActive ? 'deactivated' : 'activated'} ✅`,
        'success'
      );
      fetchParagraphs();
    } catch (err) {
      showAlert('Failed to toggle status', 'error');
    }
  };

  // ── Subscription handlers ──────────────────────────────────────
  const handleGrantSubscription = async (e) => {
    e.preventDefault();
    if (!subEmail.trim()) {
      showAlert('Email is required', 'error');
      return;
    }
    setSubLoading(true);
    try {
      const res = await typingAPI.grantSubscription(subEmail.trim(), subNote.trim());
      showAlert(res.data.message, 'success');
      setSubEmail('');
      setSubNote('');
      fetchSubscriptions();
    } catch (err) {
      showAlert(
        err.response?.data?.message || 'Failed to grant subscription',
        'error'
      );
    } finally {
      setSubLoading(false);
    }
  };

  const handleRevokeSubscription = async (email) => {
    if (!window.confirm(`Revoke subscription for ${email}?`)) return;
    try {
      await typingAPI.revokeSubscription(email);
      showAlert('Subscription revoked', 'success');
      fetchSubscriptions();
    } catch (err) {
      showAlert('Failed to revoke subscription', 'error');
    }
  };

  // ── Render helpers ─────────────────────────────────────────────
  const difficultyColor = (d) =>
    d === 'easy' ? '#10b981' : d === 'medium' ? '#f59e0b' : '#ef4444';

  const accessColor = (a) => (a === 'premium' ? '#6366f1' : '#10b981');

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="mtp-container">
      <Navbar />
    <br /> <br /> <br /> <br /> 
      {/* ── Page Header ── */}
      <div className="mtp-header">
        <div>
          <h1 className="mtp-title">⌨️ Manage Typing Paragraphs</h1>
          <p className="mtp-subtitle">
            Create and manage typing practice content & subscriptions
          </p>
        </div>
        <button className="mtp-back-btn" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className="mtp-tabs">
        <button
          className={`mtp-tab ${activeTab === 'paragraphs' ? 'active' : ''}`}
          onClick={() => setActiveTab('paragraphs')}
        >
          📄 Paragraphs ({paragraphs.length})
        </button>
        <button
          className={`mtp-tab ${activeTab === 'subscriptions' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscriptions')}
        >
          💎 Subscriptions ({subscriptions.length})
        </button>
      </div>

      {/* ════════════════════════════════════════════════════════════
          TAB: PARAGRAPHS
      ════════════════════════════════════════════════════════════ */}
      {activeTab === 'paragraphs' && (
        <>
          {/* ── Create / Edit Form ── */}
          {showForm && (
            <div className="mtp-form-card">
              <h2 className="mtp-form-title">
                {editingId ? '✏️ Edit Paragraph' : '➕ Create New Paragraph'}
              </h2>

              <form onSubmit={handleSubmitForm} className="mtp-form">
                {/* Row 1: Title */}
                <div className="mtp-field full-width">
                  <label className="mtp-label">Title *</label>
                  <input
                    className="mtp-input"
                    name="title"
                    value={form.title}
                    onChange={handleFormChange}
                    placeholder="e.g. The Water Cycle"
                    required
                  />
                </div>

                {/* Row 2: Language + Difficulty + Access */}
                <div className="mtp-field">
                  <label className="mtp-label">Language *</label>
                  <select
                    className="mtp-select"
                    name="language"
                    value={form.language}
                    onChange={handleFormChange}
                  >
                    {LANGUAGES.map((l) => (
                      <option key={l} value={l}>
                        {l === 'english' ? '🇬🇧 English' : '🇮🇳 Marathi'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mtp-field">
                  <label className="mtp-label">Difficulty *</label>
                  <select
                    className="mtp-select"
                    name="difficulty"
                    value={form.difficulty}
                    onChange={handleFormChange}
                  >
                    {DIFFICULTIES.map((d) => (
                      <option key={d} value={d}>
                        {d.charAt(0).toUpperCase() + d.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mtp-field">
                  <label className="mtp-label">Access Type *</label>
                  <select
                    className="mtp-select"
                    name="accessType"
                    value={form.accessType}
                    onChange={handleFormChange}
                  >
                    {ACCESS_TYPES.map((a) => (
                      <option key={a} value={a}>
                        {a === 'free' ? '🆓 Free' : '💎 Premium'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Row 3: Topic + Estimated Time */}
                <div className="mtp-field">
                  <label className="mtp-label">Topic *</label>
                  <input
                    className="mtp-input"
                    name="topic"
                    value={form.topic}
                    onChange={handleFormChange}
                    placeholder="e.g. Science, Nature, History"
                    required
                  />
                </div>

                <div className="mtp-field">
                  <label className="mtp-label">
                    Estimated Time (seconds) *
                  </label>
                  <div className="mtp-timer-row">
                    {TIMER_PRESETS.map((t) => (
                      <button
                        key={t}
                        type="button"
                        className={`mtp-preset-btn ${
                          Number(form.estimatedTime) === t ? 'active' : ''
                        }`}
                        onClick={() =>
                          setForm((p) => ({ ...p, estimatedTime: t }))
                        }
                      >
                        {t >= 60 ? `${t / 60}m` : `${t}s`}
                      </button>
                    ))}
                    <input
                      type="number"
                      className="mtp-input mtp-time-input"
                      name="estimatedTime"
                      value={form.estimatedTime}
                      onChange={handleFormChange}
                      min={10}
                      max={600}
                    />
                    <span className="mtp-unit">sec</span>
                  </div>
                </div>

                {/* Row 4: Text */}
                <div className="mtp-field full-width">
                  <label className="mtp-label">
                    Text * ({form.text.length} characters)
                  </label>
                  <textarea
                    className="mtp-textarea"
                    name="text"
                    value={form.text}
                    onChange={handleFormChange}
                    placeholder="Enter the paragraph text here..."
                    rows={6}
                    required
                  />
                  {form.text.length > 0 && form.text.length < 20 && (
                    <span className="mtp-warning">
                      Minimum 20 characters required
                    </span>
                  )}
                </div>

                {/* Form Actions */}
                <div className="mtp-form-actions full-width">
                  <button
                    type="submit"
                    className="mtp-save-btn"
                    disabled={submitting}
                  >
                    {submitting
                      ? '⏳ Saving...'
                      : editingId
                      ? '💾 Update Paragraph'
                      : '✅ Create Paragraph'}
                  </button>
                  <button
                    type="button"
                    className="mtp-cancel-btn"
                    onClick={handleCancelForm}
                  >
                    ✖ Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ── Filters + Create Button ── */}
          <div className="mtp-toolbar">
            <div className="mtp-filters">
              <select
                className="mtp-filter-select"
                value={filterLang}
                onChange={(e) => setFilterLang(e.target.value)}
              >
                <option value="">🌐 All Languages</option>
                <option value="english">🇬🇧 English</option>
                <option value="marathi">🇮🇳 Marathi</option>
              </select>

              <select
                className="mtp-filter-select"
                value={filterAccess}
                onChange={(e) => setFilterAccess(e.target.value)}
              >
                <option value="">🔓 All Access</option>
                <option value="free">🆓 Free</option>
                <option value="premium">💎 Premium</option>
              </select>
            </div>

            {!showForm && (
              <button className="mtp-create-btn" onClick={openCreateForm}>
                ➕ Create New Paragraph
              </button>
            )}
          </div>

          {/* ── Paragraphs List ── */}
          {loading ? (
            <div className="mtp-loading">⏳ Loading paragraphs...</div>
          ) : paragraphs.length === 0 ? (
            <div className="mtp-empty">
              <p>📭 No paragraphs found.</p>
              <button className="mtp-create-btn" onClick={openCreateForm}>
                ➕ Create your first paragraph
              </button>
            </div>
          ) : (
            <div className="mtp-list">
              {paragraphs.map((para) => (
                <div
                  key={para._id}
                  className={`mtp-card ${!para.isActive ? 'inactive' : ''}`}
                >
                  {/* Card Header */}
                  <div className="mtp-card-header">
                    <div className="mtp-card-title-row">
                      <h3 className="mtp-card-title">{para.title}</h3>
                      {!para.isActive && (
                        <span className="mtp-inactive-badge">Inactive</span>
                      )}
                    </div>
                    <div className="mtp-card-badges">
                      <span
                        className="mtp-badge"
                        style={{ backgroundColor: `${difficultyColor(para.difficulty)}20`,
                                 color: difficultyColor(para.difficulty),
                                 borderColor: difficultyColor(para.difficulty) }}
                      >
                        {para.difficulty}
                      </span>
                      <span
                        className="mtp-badge"
                        style={{ backgroundColor: `${accessColor(para.accessType)}20`,
                                 color: accessColor(para.accessType),
                                 borderColor: accessColor(para.accessType) }}
                      >
                        {para.accessType === 'premium' ? '💎 Premium' : '🆓 Free'}
                      </span>
                      <span className="mtp-badge mtp-lang-badge">
                        {para.language === 'english' ? '🇬🇧' : '🇮🇳'}{' '}
                        {para.language}
                      </span>
                    </div>
                  </div>

                  {/* Card Meta */}
                  <div className="mtp-card-meta">
                    <span>📁 {para.topic}</span>
                    <span>⏱️ {para.estimatedTime}s</span>
                    <span>📝 {para.text.length} chars</span>
                    <span>
                      🗓️{' '}
                      {new Date(para.createdAt).toLocaleDateString('en-IN')}
                    </span>
                  </div>

                  {/* Text Preview */}
                  <p className="mtp-card-preview">
                    {para.text.length > 120
                      ? para.text.slice(0, 120) + '...'
                      : para.text}
                  </p>

                  {/* Card Actions */}
                  <div className="mtp-card-actions">
                    <button
                      className="mtp-action-btn edit"
                      onClick={() => openEditForm(para)}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="mtp-action-btn toggle"
                      onClick={() => handleToggle(para)}
                    >
                      {para.isActive ? '🔴 Deactivate' : '🟢 Activate'}
                    </button>
                    <button
                      className="mtp-action-btn delete"
                      onClick={() => handleDelete(para)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════════════
          TAB: SUBSCRIPTIONS
      ════════════════════════════════════════════════════════════ */}
      {activeTab === 'subscriptions' && (
        <div className="mtp-sub-section">

          {/* Grant Form */}
          <div className="mtp-sub-form-card">
            <h2 className="mtp-form-title">💎 Grant Subscription</h2>
            <form onSubmit={handleGrantSubscription} className="mtp-sub-form">
              <div className="mtp-field">
                <label className="mtp-label">User Email *</label>
                <input
                  className="mtp-input"
                  type="email"
                  value={subEmail}
                  onChange={(e) => setSubEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div className="mtp-field">
                <label className="mtp-label">Note (optional)</label>
                <input
                  className="mtp-input"
                  value={subNote}
                  onChange={(e) => setSubNote(e.target.value)}
                  placeholder="e.g. Paid via UPI, Promo grant..."
                />
              </div>
              <button
                type="submit"
                className="mtp-save-btn"
                disabled={subLoading}
              >
                {subLoading ? '⏳ Granting...' : '✅ Grant 1-Year Subscription'}
              </button>
            </form>
          </div>

          {/* Subscriptions List */}
          <h2 className="mtp-form-title" style={{ marginTop: '2rem' }}>
            📋 All Subscriptions ({subscriptions.length})
          </h2>

          {subscriptions.length === 0 ? (
            <div className="mtp-empty">📭 No subscriptions yet.</div>
          ) : (
            <div className="mtp-sub-list">
              {subscriptions.map((sub) => {
                const isExpired = new Date() > new Date(sub.endDate);
                const isValid   = sub.isActive && !isExpired;
                const daysLeft  = isValid
                  ? Math.ceil(
                      (new Date(sub.endDate) - new Date()) /
                        (1000 * 60 * 60 * 24)
                    )
                  : 0;

                return (
                  <div
                    key={sub._id}
                    className={`mtp-sub-card ${!isValid ? 'expired' : ''}`}
                  >
                    <div className="mtp-sub-card-left">
                      <img
                        src={sub.user?.picture || 'https://via.placeholder.com/40'}
                        alt={sub.user?.name}
                        className="mtp-sub-avatar"
                      />
                      <div>
                        <p className="mtp-sub-name">{sub.user?.name}</p>
                        <p className="mtp-sub-email">{sub.user?.email}</p>
                        {sub.note && (
                          <p className="mtp-sub-note">📝 {sub.note}</p>
                        )}
                      </div>
                    </div>

                    <div className="mtp-sub-card-right">
                      <span
                        className={`mtp-sub-status ${
                          isValid ? 'active' : 'inactive'
                        }`}
                      >
                        {isValid
                          ? `✅ Active (${daysLeft}d left)`
                          : isExpired
                          ? '⏰ Expired'
                          : '🔴 Revoked'}
                      </span>
                      <p className="mtp-sub-dates">
                        {new Date(sub.startDate).toLocaleDateString('en-IN')} →{' '}
                        {new Date(sub.endDate).toLocaleDateString('en-IN')}
                      </p>
                      {isValid && (
                        <button
                          className="mtp-action-btn delete"
                          onClick={() => handleRevokeSubscription(sub.user?.email)}
                        >
                          🚫 Revoke
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManageTypingParagraphs;